import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Hooks
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useApp } from 'Shared/utils/hooks/use-app';

// Constants
import { billingStates } from 'Shared/constants/constants';

// Actions
import { fetchActiveSubscription } from 'Shared/actions/Subscriptions.actions';
import { getBillingDataAction } from 'Shared/actions/organizations';

// Components
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import Spinner from 'Shared/components/Spinner';
import Plan from './Plan/Plan';
import InvoicesHistory from './InvoicesHistory/InvoicesHistory';
import CreditCardInfo from './CreditCardInfo';
import PlanSubscription from './PlanSubscription/PlanSubscription';
import BillingContactInfo from './BillingContactInfo/BillingContactInfo';

import './BillingPage.scss';

function BillingPage() {
  const { featureFlagHideLegacyPaidBillingPage: hideLegacyPaidBillingPage, pricingUiV2: showNewPlansInfo } = useFlags();

  const { app } = useApp();
  const stripePublishableKey = app.stripePublishableKey;
  const { isOrgAdmin: isOrganizationAdmin } = useCurrentUser();
  const { billingState, features } = useOrganization();

  const [isLoading, setIsLoading] = useState(true);
  const [stripePromise] = useState(loadStripe(stripePublishableKey));
  const [activeSubscription, setActiveSubscription] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getBillingDataAction());
  }, []);

  // Billing state in depdency array so that this will update whenever a user changes plans
  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchActiveSubscription())
      .then(data => {
        setActiveSubscription(data);
      })
      .catch(e => {
        WpToastManager.danger(e.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, billingState, features]);

  const baseClassName = 'BillingPage';

  if (billingState !== billingStates.PAID_PLAN || hideLegacyPaidBillingPage) return <BillingContactInfo />;

  return (
    <div className={baseClassName}>
      {isLoading ? (
        <Spinner activeIf />
      ) : (
        <>
          <div>
            <Elements stripe={stripePromise}>
              <div className={`${baseClassName}__section`}>
                {showNewPlansInfo ? (
                  <PlanSubscription subscription={activeSubscription} />
                ) : (
                  <Plan subscription={activeSubscription} />
                )}
              </div>
              {activeSubscription && isOrganizationAdmin && (
                <>
                  <div className={`${baseClassName}__section`}>
                    <CreditCardInfo subscription={activeSubscription} setActiveSubscription={setActiveSubscription} />
                  </div>
                  <div className={`${baseClassName}__section`}>
                    <InvoicesHistory invoices={activeSubscription.invoices} />
                  </div>
                </>
              )}
            </Elements>
            <p className={`${baseClassName}__note`}>
              Have questions? Visit our{' '}
              <a
                href="https://docs.predictiveindex.com/admin-guide/subscriptions-and-billing/upgrading-your-account/"
                target="_blank"
                rel="noreferrer"
              >
                Billing FAQ
              </a>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default BillingPage;
